import React, { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, value, onChange, textarea }) => (
  <label className="block mb-3">
    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-teal-500"
        data-testid={`partner-field-${label.replace(/\s+/g, '-').toLowerCase()}`}
      />
    ) : (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-teal-500"
        data-testid={`partner-field-${label.replace(/\s+/g, '-').toLowerCase()}`}
      />
    )}
  </label>
);

const Toggle = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 text-xs text-gray-700 mb-2 cursor-pointer">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    {label}
  </label>
);

export default function PartnersTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetch(`${API}/api/partners/bimini`)
      .then((r) => r.json())
      .then(setCfg)
      .catch((err) => console.error('Failed to load partner config:', err));
  }, []);

  const set = (key, value) => setCfg((c) => ({ ...c, [key]: value }));
  const setNested = (key, sub, value) => setCfg((c) => ({ ...c, [key]: { ...(c[key] || {}), [sub]: value } }));

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const token = sessionStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/partners/bimini`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: cfg.enabled,
          popup_enabled: cfg.popup_enabled,
          destination_url: cfg.destination_url,
          guide_url: cfg.guide_url,
          disclosure: cfg.disclosure,
          discount: cfg.discount,
          feature_copy: cfg.feature_copy,
          popup_copy: cfg.popup_copy,
          eligibility: cfg.eligibility,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCfg(await res.json());
      setMsg('Saved');
    } catch (err) {
      console.error('Partner config save failed:', err);
      setMsg('Save failed — check admin session');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  if (!cfg) return <p className="text-xs text-gray-400 p-6">Loading partner config…</p>;

  const csv = (arr) => (arr || []).join(', ');
  const fromCsv = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 max-w-3xl" data-testid="partners-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Bimini — Featured Recovery Partner</h2>
          <p className="text-[11px] text-gray-400">Controls the partner feature modules + awareness popup site-wide.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-60"
          data-testid="partner-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
      {msg && <p className="text-xs mb-3 text-teal-600" data-testid="partner-save-msg">{msg}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <Toggle label="Feature modules enabled" value={cfg.enabled} onChange={(v) => set('enabled', v)} testid="partner-enabled-toggle" />
          <Toggle label="Awareness popup enabled" value={cfg.popup_enabled} onChange={(v) => set('popup_enabled', v)} testid="partner-popup-toggle" />
          <Field label="Destination URL" value={cfg.destination_url || ''} onChange={(v) => set('destination_url', v)} />
          <Field label="Guide URL" value={cfg.guide_url || ''} onChange={(v) => set('guide_url', v)} />
          <Field label="Disclosure" textarea value={cfg.disclosure || ''} onChange={(v) => set('disclosure', v)} />
          <Toggle label="Show discount" value={cfg.discount?.active} onChange={(v) => setNested('discount', 'active', v)} testid="partner-discount-toggle" />
          <Field label="Discount label" value={cfg.discount?.label || ''} onChange={(v) => setNested('discount', 'label', v)} />
        </div>
        <div>
          <Field label="Feature headline" value={cfg.feature_copy?.headline || ''} onChange={(v) => setNested('feature_copy', 'headline', v)} />
          <Field label="Feature body" textarea value={cfg.feature_copy?.body || ''} onChange={(v) => setNested('feature_copy', 'body', v)} />
          <Field label="Popup heading" value={cfg.popup_copy?.heading || ''} onChange={(v) => setNested('popup_copy', 'heading', v)} />
          <Field label="Popup body" textarea value={cfg.popup_copy?.body || ''} onChange={(v) => setNested('popup_copy', 'body', v)} />
          <Field label="Eligible categories (comma-sep)" value={csv(cfg.eligibility?.categories)} onChange={(v) => setNested('eligibility', 'categories', fromCsv(v))} />
          <Field label="Eligible tags (comma-sep)" textarea value={csv(cfg.eligibility?.tags)} onChange={(v) => setNested('eligibility', 'tags', fromCsv(v))} />
          <Field label="Excluded article slugs (editorial override)" textarea value={csv(cfg.eligibility?.excluded_slugs)} onChange={(v) => setNested('eligibility', 'excluded_slugs', fromCsv(v))} />
        </div>
      </div>
    </div>
  );
}
